"use client";

import { useUser } from "@clerk/tanstack-react-start";
import { Link } from "@tanstack/react-router";
import { X } from "lucide-react";
import { useState } from "react";

import { Button } from "#/components/ui/button";
import { useUpdatesBannerVisibility } from "#/components/useUpdatesBannerVisibility";
import { persistUpdatesSeen } from "#/lib/persistUpdatesSeen";

type UpdatesBannerProps = {
  latestUpdate: {
    id: string;
    title: string;
  };
  serverLastSeenUpdateId: string | null;
};

export function UpdatesBanner({ latestUpdate, serverLastSeenUpdateId }: UpdatesBannerProps) {
  const { isSignedIn } = useUser();
  const [dismissed, setDismissed] = useState(false);
  const { visible } = useUpdatesBannerVisibility({
    latestUpdateId: latestUpdate.id,
    serverLastSeenUpdateId,
  });

  if (!visible || dismissed) {
    return null;
  }

  const markSeen = () => {
    setDismissed(true);
    void persistUpdatesSeen({ updateId: latestUpdate.id, isSignedIn: Boolean(isSignedIn) });
  };

  return (
    <div
      role="alert"
      className="flex w-full items-center gap-3 overflow-hidden border border-border bg-card py-2 pr-2 pl-3"
    >
      <p className="min-w-0 flex-1 truncate text-sm">
        <span className="font-medium text-accent">New update: </span>
        <span className="text-foreground">{latestUpdate.title}</span>
      </p>
      <Link
        to="/updates"
        preload={false}
        className="shrink-0 text-sm font-medium text-accent underline underline-offset-2 hover:text-foreground"
        onClick={markSeen}
      >
        Read more
      </Link>
      <Button
        variant="ghost"
        size="icon-sm"
        className="shrink-0"
        aria-label="Dismiss what's new"
        onClick={markSeen}
      >
        <X />
      </Button>
    </div>
  );
}
